import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from './useOnlineStatus';
import { useWarnOnUnloadWithPendingWork } from './useWarnOnUnloadWithPendingWork';
import { getSessionStorageUser } from '../utils/sessionStorage';
import { syncOfflineQueue } from './offlineSyncManager';
import { syncCompoundQueue } from './offlineCompoundSyncManager';
import { withScreenAwake } from './nativeKeepAwake';
import { syncImageUploadQueue } from './offlineImageUploadSyncManager';
import { client } from '../gql/client';
import { showErrorToast } from '../utils/toasts';
import { isOnlineNow } from './connectivity';
import { isBackendReachable } from './backendReachability';
import { isActuallyOnline } from './checkConnectivity';
import { useDrainScheduler } from './drainScheduler';
import { LostImagesNotice } from './LostImagesNotice';
import {
  ActionButton,
  BannerStack,
  CloseButton,
  Message,
  Snackbar,
  StatusDot,
} from './offlineBannerStyles';

// ── How often to re-show the snackbar after user dismisses it (ms) ──
const REMINDER_INTERVAL = 30_000;

// ── Component ──
export function OfflineBanner() {
  const { t } = useTranslation('common');
  const { isOnline, pendingCount, failedCount, foreignCount: queuedForOthers } = useOnlineStatus();
  // Sin sesión (pantalla de login) lo encolado no es «de otro usuario»: es de
  // quien tiene que volver a entrar, y el login ya se lo dice. Con CUALQUIER
  // sesión dentro, sí (también una sin tenant, que no puede enviarlo).
  const foreignCount = getSessionStorageUser() ? queuedForOthers : 0;
  const [isSyncing, setIsSyncing] = useState(false);
  // Progreso del lote de fotos en vuelo. `null` = no hay lote o ya terminó.
  const [photoProgress, setPhotoProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [exiting, setExiting] = useState(false);
  const reminderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Avisar antes de cerrar la pestaña si queda trabajo sin enviar. Vive aquí
  // porque este componente ya está montado siempre (App.tsx) y ya tiene los
  // contadores; montarlo aparte significaría releer las colas por duplicado.
  // Ojo: más abajo hay returns condicionales que dejan de PINTAR el banner,
  // pero el componente sigue montado, así que el aviso sigue activo.
  useWarnOnUnloadWithPendingWork(pendingCount + failedCount);

  // 🔴 Estado de sincronización en un REF, no solo en el `useState`.
  //
  // El guard `if (isSyncing) return` leía el estado de React, que dentro de un
  // `useCallback` puede ir un render por detrás, y además DESCARTABA la
  // petición: si mientras se drena llega un lote nuevo (el operario guarda una
  // actuación con fotos), ese aviso se perdía y las fotos se quedaban en la
  // cola hasta que algo volviera a disparar el sync.
  //
  // Visto en vivo: encolar 12 fotos arrancaba el drenado con 2 y las otras 10
  // se quedaban colgadas, porque su aviso llegó con el sync ya en marcha.
  //
  // Ahora la petición no se tira: se anota y se relanza al terminar.
  const sincronizando = useRef(false);
  const pedidoMientrasSincronizaba = useRef<
    { resetFailed?: boolean } | null
  >(null);

  const handleSync = useCallback(async (opts?: { resetFailed?: boolean }) => {
    if (!client) return { synced: 0 };
    if (sincronizando.current) {
      // Fusionar: si alguna de las peticiones pedía resetFailed, se respeta.
      pedidoMientrasSincronizaba.current = {
        resetFailed:
          pedidoMientrasSincronizaba.current?.resetFailed || opts?.resetFailed,
      };
      return { synced: 0 };
    }
    sincronizando.current = true;
    setIsSyncing(true);
    setDismissed(false);
    try {
      // 🔴 Con el servidor dado por caído, primero se pregunta (spec 2026-10-09
      // § 9). Los disparadores de abajo miran si el APARATO tiene red, no si
      // contesta el servidor, y una pasada en ese estado era peor que ninguna:
      // las fotos y el croquis de la compound gastaban intentos sin salir a la
      // red (a los 5, aparcadas), la cola simple se re-encolaba y la compound sí
      // salía, con lecturas de caché y sin el tramo real de la simple. Si el
      // servidor ha vuelto, el sondeo cierra el modo y se drena con todo en orden.
      if (isBackendReachable() === false && !(await isActuallyOnline())) {
        return { synced: 0 };
      }
      // Orden importante: imágenes primero — pueden crear refs DB nuevos
      // que mutations encoladas en simple/compound podrían referenciar.
      // `resetFailed` solo se pasa en sync manual (botón) para reintentar
      // poison pills; en auto-sync (online event) se respetan los failed.
      //
      // `withScreenAwake` envuelve SOLO la subida de imágenes, no las colas
      // simple y compound que van detrás: en el APK, apagar la pantalla congela
      // el JS del WebView y corta la subida en vuelo. En web es un no-op.
      const imageResult = await withScreenAwake(() =>
        syncImageUploadQueue(client, { resetFailed: opts?.resetFailed }),
      );
      const simpleResult = await syncOfflineQueue(client);
      const compoundResult = await syncCompoundQueue(client, { resetFailed: opts?.resetFailed });

      const totalSynced =
        (imageResult?.synced ?? 0) +
        (simpleResult?.synced ?? 0) +
        (compoundResult?.synced ?? 0);
      if (totalSynced > 0) {
        window.dispatchEvent(new CustomEvent('offline-sync-complete', {
          detail: { synced: totalSynced },
        }));
      }
      return { synced: totalSynced };
    } finally {
      sincronizando.current = false;
      setIsSyncing(false);

      // Si llegó trabajo nuevo mientras drenábamos, atenderlo ahora. Es UNA
      // relanzada, no un bucle: la petición se consume antes de volver a
      // llamar, así que solo se repite si vuelve a entrar trabajo nuevo.
      const pendiente = pedidoMientrasSincronizaba.current;
      if (pendiente) {
        pedidoMientrasSincronizaba.current = null;
        void handleSyncRef.current?.(pendiente);
      }
    }
    // Sin dependencias: el estado de reentrada vive en refs, así que la
    // función es estable y los efectos que la usan no se re-suscriben en cada
    // cambio de `isSyncing`.
  }, []);

  // Referencia estable para poder relanzarse a sí misma desde el `finally`.
  const handleSyncRef = useRef(handleSync);
  handleSyncRef.current = handleSync;

  // Auto-sync when coming back online
  useEffect(() => {
    // Sin mirar `isSyncing`: si la reconexión llega con una pasada en marcha,
    // `handleSync` la apunta y la relanza al terminar. Antes se perdía y lo que
    // quedara esperaba al planificador (hasta 5 min; revisión del 9-oct).
    if (isOnline && pendingCount > 0) {
      setDismissed(false); // Show snackbar when reconnecting
      handleSync(); // sin resetFailed: respetamos poison pills automáticamente
    }
  }, [isOnline]);

  // 🔴 Drenado AL ARRANCAR con trabajo pendiente.
  //
  // El auto-sync de arriba depende de que CAMBIE `isOnline`. Al abrir la app
  // ya estás online, así que no cambia nunca y nada dispara la cola: la app
  // arranca, el banner dice «3 pendientes» y ahí se quedan hasta que el
  // operario pulse Sincronizar o la red dé un tumbo.
  //
  // Se nota justo cuando más duele: el operario cierra la app (o Android la
  // mata) con fotos a medio subir y al reabrirla no sube nada solo.
  //
  // Medido: arranque en frío con 3 fotos pendientes y red → 20 s después
  // seguían las 3 sin tocar.
  //
  // El `useRef` lo limita a UN disparo por montaje: `pendingCount` va bajando
  // según drena, y sin el guard cada cambio relanzaría el sync.
  // 🔴 UN SOLO disparo, poco después de montar — y NO reactivo a
  // `pendingCount`.
  //
  // La primera versión dependía de `pendingCount`, y ese contador sube según
  // se van encolando las fotos UNA A UNA. Resultado: el drenado arrancaba a
  // mitad del encolado, con 2 ó 3 dentro, y el resto se quedaba fuera de la
  // tanda. Medido: encolar 12 disparaba el sync con 2 y las otras 10 se
  // quedaban colgadas — el operario veía «3 subiendo» y luego «9 pendientes».
  //
  // El temporizador da tiempo a que `useOnlineStatus` lea las tres colas (una
  // lectura de IndexedDB) y, sobre todo, cierra la ventana: pasado el arranque
  // este efecto no vuelve a dispararse nunca, así que no puede colarse en
  // medio de un encolado. Lo que llegue después lo cubren el evento de lote y
  // el auto-sync por reconexión.
  const pendientesAhora = useRef(pendingCount);
  pendientesAhora.current = pendingCount;

  useEffect(() => {
    const alArrancar = setTimeout(() => {
      if (isOnlineNow() && pendientesAhora.current > 0) {
        handleSync(); // sin resetFailed: los poison pills se respetan
      }
    }, 2000);
    return () => clearTimeout(alArrancar);
    // Solo al montar.
  }, [handleSync]);

  // Drenado al encolar un lote de fotos (outbox 2026-08).
  //
  // El efecto de arriba solo reacciona a `isOnline`, así que guardar una
  // actuación CON cobertura encolaba las fotos y no las subía nadie hasta el
  // siguiente corte de red. `saveData` no llama al sync directamente porque
  // tiene varios puntos de retorno y sembrar la llamada en todos se olvida al
  // añadir el siguiente.
  useEffect(() => {
    const alEncolarLote = () => {
      if (isOnlineNow()) handleSync();
    };
    window.addEventListener('offline-image-batch-queued', alEncolarLote);
    return () =>
      window.removeEventListener('offline-image-batch-queued', alEncolarLote);
  }, [handleSync]);

  // Reanudar al volver a primer plano.
  //
  // En el APK, Android congela el JS del WebView al apagar la pantalla o
  // cambiar de app, y una subida en curso se corta. La cola es duradera y va
  // foto a foto, así que reanudar al volver cuesta como mucho repetir UNA. Sin
  // esto, lo que quedó a medias esperaría a un evento de reconexión que puede
  // no llegar nunca (la cobertura nunca se fue: fue la app la que se durmió).
  useEffect(() => {
    const alVolver = () => {
      if (document.visibilityState === 'visible' && isOnlineNow()) {
        handleSync();
      }
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [handleSync]);

  // Progreso real del lote, emitido por el sync manager foto a foto.
  useEffect(() => {
    const alProgresar = (e: Event) => {
      const detalle = (e as CustomEvent<{ done: number; total: number }>)
        .detail;
      if (!detalle || detalle.total === 0) {
        setPhotoProgress(null);
        return;
      }
      setPhotoProgress(
        detalle.done >= detalle.total ? null : { ...detalle },
      );
    };
    window.addEventListener('offline-image-progress', alProgresar);
    return () =>
      window.removeEventListener('offline-image-progress', alProgresar);
  }, []);

  // Reintento con retroceso (2026-09-08): lo encolado con la app ONLINE —la
  // red degradada— no dispara ninguno de los efectos de arriba. Mientras haya
  // pendientes y red, se insiste (15 s → 5 min) hasta que suba.
  useDrainScheduler({
    active: isOnline && pendingCount > 0 && !isSyncing,
    drain: () => handleSync(),
  });

  // 🔴 PÉRDIDA SILENCIOSA (arreglado 2026-08-03).
  //
  // Los tres sync managers y varios `saveData` emiten `offline-sync-error`
  // cuando descartan una operación encolada (poison pill: validación/400, que
  // nunca tendrá éxito) y `offline-sync-auth-required` cuando la sesión ha
  // caducado a mitad del envío. Nueve puntos de emisión… y **NADIE los
  // escuchaba**: `grep` no encontraba un solo `addEventListener` para ninguno
  // de los dos. Resultado: el trabajo que la cola tiraba desaparecía sin que
  // el operario viera absolutamente nada.
  //
  // Este banner es el sitio natural: se monta en `App.tsx`, o sea siempre.
  // Se pinta con el toast de error estándar de la app y con el MENSAJE que
  // trae el propio evento (cada emisor ya sabe qué se perdió).
  //
  // Un emisor puede mandar la CLAVE (`messageKey`) en vez del texto, y aquí se
  // traduce: la cola compound lo hace desde 2026-09-28 para no cargar i18n en
  // la lógica offline (spec cola-compound-y-latido § 9).
  useEffect(() => {
    const alDescartar = (e: Event) => {
      const detalle = (e as CustomEvent<{ message?: string; messageKey?: string }>).detail;
      const texto = detalle?.messageKey ? t(detalle.messageKey) : detalle?.message;
      if (texto) showErrorToast(texto);
    };
    window.addEventListener('offline-sync-error', alDescartar);
    window.addEventListener('offline-sync-auth-required', alDescartar);
    return () => {
      window.removeEventListener('offline-sync-error', alDescartar);
      window.removeEventListener('offline-sync-auth-required', alDescartar);
    };
  }, [t]);

  // Re-show snackbar periodically after dismiss
  // Lo ajeno también: no se envía, pero un cierre no puede ocultarlo para
  // siempre (spec 2026-10-01 § 6.3 «Que se vea»).
  useEffect(() => {
    if (dismissed && (!isOnline || pendingCount > 0 || failedCount > 0 || foreignCount > 0)) {
      reminderTimer.current = setTimeout(() => {
        setDismissed(false);
      }, REMINDER_INTERVAL);
    }
    return () => {
      if (reminderTimer.current) clearTimeout(reminderTimer.current);
    };
  }, [dismissed, isOnline, pendingCount, failedCount, foreignCount]);

  // Reset dismissed state when going offline
  useEffect(() => {
    if (!isOnline) setDismissed(false);
  }, [isOnline]);

  const handleDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setDismissed(true);
      setExiting(false);
    }, 250);
  }, []);

  // ── Render nothing if online + nothing pending/failed + not syncing ──
  // `photoProgress` cuenta: mientras haya fotos subiendo el banner se queda,
  // aunque la cola ya no marque pendientes.
  // Sin `return null` aquí: el aviso de imágenes perdidas se pinta aunque el
  // banner no tenga nada que enseñar. Lo ajeno también cuenta (spec 2026-10-01
  // § 6.3 «Que se vea»).
  const showBanner =
    !dismissed &&
    !(
      isOnline &&
      pendingCount === 0 &&
      failedCount === 0 &&
      foreignCount === 0 &&
      !isSyncing &&
      !photoProgress
    );

  // ── Determine variant ──
  // Lo ajeno no es un error: es trabajo esperando a su dueño → tono de
  // pendiente, no el naranja de «algo ha fallado».
  // `failed` (naranja) solo cuando NO hay pendientes reintentables: son
  // subidas en error terminal que el auto-sync no toca — el usuario las
  // reintenta con el botón. Si hay pendientes, prima `pending` (se sincronizan
  // solos) y el botón cubre ambos.
  // `photoProgress` va ANTES que `pendingCount`/`failed`: subir fotos es
  // trabajo EN CURSO, no un error. Sin esta rama, un lote en vuelo sin
  // pendientes en cola caía a `failed` y el operario veía el naranja de
  // «algo ha fallado» junto al texto «Subiendo fotos · 7 de 20» — cazado
  // mirando la pantalla, no por los tests.
  const variant: 'offline' | 'pending' | 'syncing' | 'failed' = !isOnline
    ? 'offline'
    : isSyncing || photoProgress
      ? 'syncing'
      : pendingCount > 0 || (failedCount === 0 && foreignCount > 0)
        ? 'pending'
        : 'failed';

  // ── Build message ──
  let message: string;
  if (!isOnline) {
    message = pendingCount > 0
      ? `${t('offline.noConnection')} · ${t('offline.pendingItems', { count: pendingCount })}`
      : t('offline.noConnection');
  } else if (photoProgress) {
    // Prioridad sobre «sincronizando»: durante una subida de 20 fotos con
    // cobertura mala, un mensaje genérico sin números es exactamente lo que
    // hace pensar al operario que la app se ha colgado.
    message = t('offline.uploadingPhotos', {
      done: photoProgress.done,
      total: photoProgress.total,
    });
  } else if (isSyncing) {
    message = t('offline.syncing');
  } else if (pendingCount > 0) {
    message = t('offline.pendingItems', { count: pendingCount });
  } else if (failedCount > 0) {
    message = t('offline.failedItems', { count: failedCount });
  } else {
    message = t('offline.foreignItems', { count: foreignCount });
  }

  return (
    <BannerStack>
      <LostImagesNotice />
      {showBanner && (
        <Snackbar $variant={variant} $exiting={exiting}>
          <StatusDot $variant={variant} />
          <Message>{message}</Message>
          {isOnline && (pendingCount > 0 || failedCount > 0) && !isSyncing && (
            <ActionButton onClick={() => handleSync({ resetFailed: true })}>
              {pendingCount > 0 ? t('offline.syncNow') : t('offline.retry')}
            </ActionButton>
          )}
          {!isSyncing && (
            <CloseButton onClick={handleDismiss} aria-label="close">
              &times;
            </CloseButton>
          )}
        </Snackbar>
      )}
    </BannerStack>
  );
}
